/**
 * Die Diagramme des Dashboards.
 *
 * Aus HTML statt SVG gebaut: Text bleibt Text (scharf, gross genug, im
 * Dunkelmodus lesbar), und alles passt sich der Breite des Handys an. Die
 * Regeln stammen aus der dataviz-Richtlinie:
 *
 * - Balken hoechstens 24 px dick, 4 px gerundet am Ende, gerade an der Basis.
 * - 2 px Luft zwischen aneinanderstossenden Flaechen -- die Luft trennt, kein
 *   Rahmen.
 * - Text traegt nie die Farbe der Daten; die Farbe sitzt in einem Punkt daneben.
 * - Jeder Wert ist auch ohne Hovern erreichbar: ueber die Beschriftung, den
 *   Fokus oder die Liste dahinter (Rueckmeldungen 8.1). Die Tabellen unter den
 *   Diagrammen sind auf Wunsch der Gruppe entfallen (8.2).
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router";

/** Zahlen deutsch geschrieben: 1.284 */
export const zahl = (n: number): string => n.toLocaleString("de-DE");

// --- Hinweis beim Beruehren ---------------------------------------------------

interface Hinweis {
  x: number;
  y: number;
  wert: string;
  text: string;
}

/**
 * Ein gemeinsamer Hinweis je Diagramm.
 *
 * Wert vorn und kraeftig, die Bezeichnung dahinter -- wer hinzeigt, kennt die
 * Reihe schon und will die Zahl.
 */
function useHinweis() {
  const [hinweis, setHinweis] = useState<Hinweis | null>(null);

  const an = (wert: string, text: string) => ({
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      const rahmen = e.currentTarget.closest("[data-diagramm]")?.getBoundingClientRect();
      if (rahmen === undefined) return;
      setHinweis({ x: e.clientX - rahmen.left, y: e.clientY - rahmen.top, wert, text });
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      const rahmen = e.currentTarget.closest("[data-diagramm]")?.getBoundingClientRect();
      const ziel = e.currentTarget.getBoundingClientRect();
      if (rahmen === undefined) return;
      setHinweis({
        x: ziel.left - rahmen.left + ziel.width / 2,
        y: ziel.top - rahmen.top,
        wert,
        text,
      });
    },
    onPointerLeave: () => {
      setHinweis(null);
    },
    onBlur: () => {
      setHinweis(null);
    },
  });

  const anzeige =
    hinweis === null ? null : (
      <div
        className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-neutral-900 px-3 py-2 text-sm whitespace-nowrap text-white shadow-lg dark:bg-neutral-100 dark:text-neutral-900"
        style={{ left: hinweis.x, top: hinweis.y - 8 }}
        role="tooltip"
      >
        <span className="font-bold">{hinweis.wert}</span>{" "}
        <span className="opacity-80">{hinweis.text}</span>
      </div>
    );

  return { an, anzeige };
}

// --- Kennzahl und Leitzahl ------------------------------------------------------

export type Ton = "neutral" | "gut" | "warnung" | "kritisch";

/** Symbol und Farbe einer Einschaetzung -- nie Farbe allein. */
export const TON = {
  neutral: { zeichen: "", farbe: "var(--ohne)", wort: "" },
  gut: { zeichen: "✓", farbe: "var(--gut)", wort: "in Ordnung" },
  warnung: { zeichen: "!", farbe: "var(--warnung)", wort: "Achtung" },
  kritisch: { zeichen: "✕", farbe: "var(--kritisch)", wort: "kritisch" },
} as const;

export function TonZeichen({ ton }: { ton: Ton }) {
  if (ton === "neutral") return null;
  return (
    <span
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
      style={{ backgroundColor: TON[ton].farbe }}
      aria-label={TON[ton].wort}
    >
      {TON[ton].zeichen}
    </span>
  );
}

/** Eine Kachel mit einer Zahl. Antippbar, wenn es eine Liste dahinter gibt. */
export function Kennzahl({
  bezeichnung,
  wert,
  einheit,
  ton = "neutral",
  link,
}: {
  bezeichnung: string;
  wert: number;
  einheit?: string | undefined;
  ton?: Ton;
  link?: string | undefined;
}) {
  const inhalt = (
    <>
      <span className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
        <TonZeichen ton={ton} />
        {bezeichnung}
      </span>
      <span className="text-2xl font-semibold">
        {zahl(wert)}
        {einheit !== undefined && <span className="ml-1 text-base font-normal">{einheit}</span>}
      </span>
    </>
  );
  const klasse =
    "flex min-h-20 flex-col justify-between gap-1 rounded-xl bg-white p-3 dark:bg-neutral-800";
  return link === undefined ? (
    <div className={klasse}>{inhalt}</div>
  ) : (
    <Link to={link} className={`${klasse} active:bg-neutral-100 dark:active:bg-neutral-700`}>
      {inhalt}
    </Link>
  );
}

/**
 * Die eine Zahl, mit der das Dashboard beginnt -- gross, mit Anteil als
 * Balken darunter. Die Spur ist eine hellere Stufe derselben Farbe.
 */
export function Leitzahl({
  bezeichnung,
  wert,
  von,
  erklaerung,
  link,
}: {
  bezeichnung: string;
  wert: number;
  von: number;
  erklaerung: string;
  /** Die Liste dahinter (Rückmeldungen 8.1). */
  link?: string | undefined;
}) {
  const anteil = von === 0 ? 0 : wert / von;
  const inhalt = (
    <>
      <h2 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">
        {bezeichnung}
      </h2>
      <p className="flex items-baseline gap-2">
        <span className="text-5xl font-semibold">{zahl(wert)}</span>
        <span className="text-lg text-neutral-600 dark:text-neutral-300">
          von {zahl(von)} · {Math.round(anteil * 100)} %
        </span>
      </p>
      <div
        className="h-3 overflow-hidden rounded-full"
        style={{ backgroundColor: "color-mix(in srgb, var(--gut) 20%, transparent)" }}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={von}
        aria-valuenow={wert}
        aria-label={bezeichnung}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${String(anteil * 100)}%`, backgroundColor: "var(--gut)" }}
        />
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{erklaerung}</p>
    </>
  );
  const klasse = "flex flex-col gap-2 rounded-xl bg-white p-4 dark:bg-neutral-800";
  return link === undefined ? (
    <section className={klasse}>{inhalt}</section>
  ) : (
    <Link to={link} className={`${klasse} active:bg-neutral-100 dark:active:bg-neutral-700`}>
      {inhalt}
    </Link>
  );
}

// --- Gestapelter Balken: Teile eines Ganzen ---------------------------------------

export interface Teil {
  name: string;
  wert: number;
  farbe: string;
  zeichen?: ReactNode;
  link?: string | undefined;
}

/**
 * Wie sich ein Ganzes aufteilt -- ein einziger Balken, darunter die Legende
 * mit Zahlen. Die Legende ist die verlaessliche Zuordnung; die Farbe hilft nur.
 */
export function StapelBalken({ teile, einheit }: { teile: readonly Teil[]; einheit: string }) {
  const { an, anzeige } = useHinweis();
  const sichtbar = teile.filter((t) => t.wert > 0);
  const gesamt = sichtbar.reduce((s, t) => s + t.wert, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative" data-diagramm>
        {gesamt === 0 ? (
          <div className="h-6 rounded bg-neutral-100 dark:bg-neutral-700" />
        ) : (
          <div className="flex h-6 gap-0.5 overflow-hidden rounded">
            {sichtbar.map((t) => (
              <div
                key={t.name}
                tabIndex={0}
                className="h-full min-w-1 outline-offset-2 hover:brightness-110"
                style={{ flexGrow: t.wert, backgroundColor: t.farbe }}
                aria-label={`${t.name}: ${zahl(t.wert)} ${einheit}`}
                {...an(zahl(t.wert), t.name)}
              />
            ))}
          </div>
        )}
        {anzeige}
      </div>

      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {teile.map((t) => {
          const zeile = (
            <>
              <span className="flex items-center gap-2">
                {t.zeichen ?? (
                  <span
                    className="inline-block size-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: t.farbe }}
                    aria-hidden
                  />
                )}
                <span>{t.name}</span>
              </span>
              <span className="font-semibold tabular-nums">
                {zahl(t.wert)}
                {gesamt > 0 && (
                  <span className="ml-2 font-normal text-neutral-500 dark:text-neutral-400">
                    {Math.round((t.wert / gesamt) * 100)} %
                  </span>
                )}
              </span>
            </>
          );
          const klasse = "flex min-h-10 items-center justify-between gap-3 rounded-lg px-1";
          return (
            <li key={t.name}>
              {t.link === undefined ? (
                <div className={klasse}>{zeile}</div>
              ) : (
                <Link
                  to={t.link}
                  className={`${klasse} active:bg-neutral-100 dark:active:bg-neutral-700`}
                >
                  {zeile}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// --- Liegende Balken: Groessen vergleichen ----------------------------------------

/**
 * Groessen vergleichen -- eine Farbe fuer alle, der Wert steht am Balkenende.
 * Mehr als eine Farbe braeuchte es nur, wenn die Balken verschiedene Dinge
 * waeren; hier sind es Mengen derselben Art.
 */
export function Balken({
  eintraege,
  farbe = "var(--reihe-1)",
}: {
  eintraege: readonly { name: string; wert: number; link?: string | undefined }[];
  farbe?: string;
}) {
  const hoechstens = Math.max(1, ...eintraege.map((e) => e.wert));
  return (
    <ul className="flex flex-col gap-2">
      {eintraege.map((e) => {
        const inhalt = (
          <>
            <span className="w-32 shrink-0 text-sm">{e.name}</span>
            <span className="flex flex-1 items-center gap-2">
              <span
                className="h-5 max-h-6 rounded-r"
                style={{
                  width: `${String((e.wert / hoechstens) * 85)}%`,
                  minWidth: e.wert > 0 ? 4 : 0,
                  backgroundColor: farbe,
                }}
                aria-hidden
              />
              <span className="text-sm font-semibold tabular-nums">{zahl(e.wert)}</span>
            </span>
          </>
        );
        const klasse = "flex min-h-10 items-center gap-2 rounded-lg";
        return (
          <li key={e.name}>
            {e.link === undefined ? (
              <div className={klasse}>{inhalt}</div>
            ) : (
              <Link
                to={e.link}
                className={`${klasse} active:bg-neutral-100 dark:active:bg-neutral-700`}
              >
                {inhalt}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// --- Saeulen ueber Tage ------------------------------------------------------------

export interface Reihe {
  name: string;
  farbe: string;
}

/** Saubere Obergrenze fuer die Achse: 1, 2, 5, 10, 20, 50 … */
function runde(n: number): number {
  if (n <= 5) return Math.max(1, Math.ceil(n));
  const stufe = 10 ** Math.floor(Math.log10(n));
  for (const f of [1, 2, 5, 10]) if (n <= f * stufe) return f * stufe;
  return 10 * stufe;
}

const tagKurz = (datum: string): string =>
  new Date(`${datum}T12:00:00`).toLocaleDateString("de-DE", { day: "numeric", month: "numeric" });

/**
 * Gestapelte Saeulen je Tag. Jede Saeule ist als Ganzes der Zielbereich --
 * niemand muss auf einen zwei Pixel breiten Streifen tippen.
 */
export function Saeulen({
  tage,
  reihen,
}: {
  tage: readonly { datum: string; werte: readonly number[] }[];
  reihen: readonly Reihe[];
}) {
  const { an, anzeige } = useHinweis();
  const summen = tage.map((t) => t.werte.reduce((s, w) => s + w, 0));
  const oben = runde(Math.max(0, ...summen));
  const beschriftet = new Set([0, Math.floor((tage.length - 1) / 2), tage.length - 1]);

  return (
    <div className="flex flex-col gap-2">
      {reihen.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {reihen.map((r) => (
            <li key={r.name} className="flex items-center gap-2">
              <span
                className="inline-block size-3 rounded-sm"
                style={{ backgroundColor: r.farbe }}
                aria-hidden
              />
              {r.name}
            </li>
          ))}
        </ul>
      )}

      <div className="relative flex gap-2" data-diagramm>
        {/* Achse: nur Null, Mitte und oben -- genug, um Groessen abzulesen. */}
        <div className="flex h-40 w-8 shrink-0 flex-col justify-between text-right text-xs text-neutral-500 tabular-nums dark:text-neutral-400">
          <span>{zahl(oben)}</span>
          <span>{zahl(oben / 2)}</span>
          <span>0</span>
        </div>
        <div className="relative h-40 flex-1">
          {[0, 50, 100].map((p) => (
            <div
              key={p}
              className="absolute inset-x-0 h-px"
              style={{ top: `${String(p)}%`, backgroundColor: "var(--gitter)" }}
              aria-hidden
            />
          ))}
          <div className="absolute inset-0 flex items-end gap-0.5">
            {tage.map((t, i) => {
              const summe = summen[i] ?? 0;
              const text = `${new Date(`${t.datum}T12:00:00`).toLocaleDateString("de-DE", {
                weekday: "short",
                day: "numeric",
                month: "numeric",
              })}: ${reihen.map((r, ri) => `${r.name} ${zahl(t.werte[ri] ?? 0)}`).join(", ")}`;
              return (
                <div
                  key={t.datum}
                  tabIndex={0}
                  className="flex h-full flex-1 flex-col-reverse items-center justify-start outline-offset-1 hover:bg-neutral-100 dark:hover:bg-neutral-700/50"
                  aria-label={text}
                  {...an(zahl(summe), text)}
                >
                  <div
                    className="flex w-full max-w-6 flex-col-reverse gap-0.5 overflow-hidden rounded-t"
                    style={{ height: `${String((summe / oben) * 100)}%` }}
                  >
                    {reihen.map((r, ri) => {
                      const w = t.werte[ri] ?? 0;
                      if (w === 0) return null;
                      return (
                        <div
                          key={r.name}
                          style={{ flexGrow: w, backgroundColor: r.farbe }}
                          aria-hidden
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {anzeige}
      </div>

      <div className="ml-10 flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
        {tage.map((t, i) =>
          beschriftet.has(i) ? <span key={t.datum}>{tagKurz(t.datum)}</span> : null,
        )}
      </div>
    </div>
  );
}
